import { titleContainment, titleVersionTags, versionTagsMatch } from "./titleMatch";

interface SpotifyTrack {
  name: string;
  artists?: { name: string }[];
}

interface YouTubeVideo {
  id: { videoId: string };
  snippet: {
    title: string;
    channelTitle: string;
    description?: string;
    publishedAt?: string;
    thumbnails?: {
      default?: { url: string };
      medium?: { url: string };
      high?: { url: string };
    };
  };
  matchScore?: number;
  matchDetails?: MatchDetails;
}

interface MatchDetails {
  trackNameMatch: boolean;
  artistNameMatch: boolean;
  channelMatchesArtist: boolean;
  isOfficialContent: boolean;
  penaltyApplied: string[];
  normalizedVideoTitle: string;
  normalizedTrackName: string;
  similarityScore: number;
}

export function normalizeText(text: string): string {
  if (!text) return "";

  return text
    .toLowerCase()
    .replace(/\s*[\(\[\{].*?[\)\]\}]\s*/g, " ")
    .replace(/\s*[-–—]\s*(official\s*)?(music\s*)?(video|audio|lyric|lyrics|visualizer|mv|live|acoustic|remix)/gi, " ")
    .replace(/\s*(official\s*)?(music\s*)?(video|audio|lyric|lyrics|visualizer|mv)/gi, " ")
    .replace(/\b(ft\.?|feat\.?|featuring|with|&|and|x)\b/gi, " ")
    .replace(/[''`]/g, "'")
    .replace(/[""„]/g, '"')
    .replace(/[-–—]/g, " ")
    .replace(/[^\w\s'"]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function extractCoreSongName(youtubeTitle: string): string {
  if (!youtubeTitle) return "";

  let title = youtubeTitle.toLowerCase();

  const patterns = [
    /^(.+?)\s*[-–—]\s*official\b/i,
    /^(.+?)\s*\|\s*/i,
    /^(.+?)\s*[-–—]\s*[\(\[]?(music\s*video|official|lyric|audio|visualizer)/i,
    /^(.+?)\s*[\(\[]?(official|music\s*video|lyric|audio)/i,
  ];

  for (const pattern of patterns) {
    const match = title.match(pattern);
    if (match && match[1]) {
      title = match[1];
      break;
    }
  }

  title = title
    .replace(/[\(\[\{]\s*(?:ft\.?|feat\.?|featuring)\s+([^\)\]\}]+)[\)\]\}]/gi, " $1 ")
    .replace(/\s*[\(\[\{].*?[\)\]\}]\s*/g, " ")
    .replace(/\s*[-–—]\s*(?:official|music video|lyric|audio|visualizer|live|acoustic|remix|mv).*$/i, "")
    .trim();

  return normalizeText(title);
}

export function calculateSimilarity(str1: string, str2: string): number {
  if (!str1 || !str2) return 0;
  if (str1 === str2) return 1;

  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();

  if (s1 === s2) return 1;
  if (s1.includes(s2) || s2.includes(s1)) {
    const ratio = Math.min(s1.length, s2.length) / Math.max(s1.length, s2.length);
    return 0.8 + (ratio * 0.2);
  }

  const tokens1 = new Set(s1.split(/\s+/).filter(t => t.length > 1));
  const tokens2 = new Set(s2.split(/\s+/).filter(t => t.length > 1));

  if (tokens1.size === 0 || tokens2.size === 0) return 0;

  const intersection = [...tokens1].filter(t => tokens2.has(t));
  const union = new Set([...tokens1, ...tokens2]);

  const jaccardSimilarity = intersection.length / union.size;

  const matrix: number[][] = [];
  for (let i = 0; i <= s1.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= s2.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= s1.length; i++) {
    for (let j = 1; j <= s2.length; j++) {
      if (s1[i - 1] === s2[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  const levenshteinDistance = matrix[s1.length][s2.length];
  const maxLength = Math.max(s1.length, s2.length);
  const levenshteinSimilarity = 1 - levenshteinDistance / maxLength;

  return (jaccardSimilarity * 0.25) + (levenshteinSimilarity * 0.75);
}

export function matchYouTubeToSpotify(
  videos: YouTubeVideo[],
  spotifyTracks: SpotifyTrack[],
  artistName: string
): YouTubeVideo[] {
  if (!videos || videos.length === 0) return [];
  if (!spotifyTracks || spotifyTracks.length === 0) return videos;

  const normalizedArtistName = normalizeText(artistName);
  const normalizedTrackNames = spotifyTracks.map(track => ({
    original: track.name,
    normalized: normalizeText(track.name),
    artists: track.artists?.map(a => normalizeText(a.name)) || [],
  }));

  const scoredVideos = videos.map(video => {
    const title = video.snippet.title;
    const channelTitle = video.snippet.channelTitle;
    const normalizedTitle = normalizeText(title);
    const coreTitle = extractCoreSongName(title);
    const normalizedChannel = normalizeText(channelTitle);

    let score = 0;
    const penalties: string[] = [];
    let bestTrackMatch = { trackNameMatch: false, normalizedTrackName: "", originalTrackName: "", similarityScore: 0 };
    let bestRank = 0;

    for (const track of normalizedTrackNames) {
      const fullSimilarity = calculateSimilarity(normalizedTitle, track.normalized);
      const coreSimilarity = calculateSimilarity(coreTitle, track.normalized);
      const containment = titleContainment(track.original, title) * 0.85;
      const similarity = Math.max(fullSimilarity, coreSimilarity, containment);
      const rank = similarity + (versionTagsMatch(track.original, title) ? 0.2 : 0);

      if (rank > bestRank) {
        bestRank = rank;
        bestTrackMatch = {
          trackNameMatch: similarity >= 0.7,
          normalizedTrackName: track.normalized,
          originalTrackName: track.original,
          similarityScore: similarity,
        };
      }
    }

    if (bestTrackMatch.trackNameMatch) {
      score += 100 * bestTrackMatch.similarityScore;
    }

    const artistInTitle = normalizedTitle.includes(normalizedArtistName);
    const artistInChannel = normalizedChannel.includes(normalizedArtistName);

    if (artistInChannel) {
      score += 80;
    }

    if (artistInTitle) {
      score += 50;
    }

    const channelIsVevo = /vevo\s*$/i.test(channelTitle);
    const channelIsTopic = /\s*-\s*topic\s*$/i.test(channelTitle);
    const hasOfficialInChannel = /official/i.test(channelTitle);

    if (channelIsVevo || channelIsTopic || hasOfficialInChannel) {
      score += 60;
    }

    const lowerTitle = title.toLowerCase();
    if (/official\s*(music\s*)?video/i.test(title)) {
      score += 30;
    } else if (/official\s*(audio|lyric)/i.test(title)) {
      score += 25;
    } else if (/music\s*video/i.test(title)) {
      score += 15;
    }

    if (/\bcover\b/i.test(lowerTitle)) {
      score -= 200;
      penalties.push("cover");
    }
    if (/\breaction\b/i.test(lowerTitle)) {
      score -= 200;
      penalties.push("reaction");
    }
    if (/\btutorial\b/i.test(lowerTitle)) {
      score -= 150;
      penalties.push("tutorial");
    }
    if (/\bkaraoke\b/i.test(lowerTitle)) {
      score -= 150;
      penalties.push("karaoke");
    }
    const matchedTrackTags = new Set(bestTrackMatch.similarityScore >= 0.5 ? titleVersionTags(bestTrackMatch.originalTrackName) : []);
    for (const tag of titleVersionTags(title)) {
      if (matchedTrackTags.has(tag) || tag === "karaoke" || tag === "cover") continue;
      score -= tag === "instrumental" || tag === "acapella" ? 100 : tag === "remix" ? 50 : 30;
      penalties.push(tag);
    }

    if (!artistInTitle && !artistInChannel) {
      score -= 100;
      penalties.push("no-artist-verification");
    }

    const matchDetails: MatchDetails = {
      trackNameMatch: bestTrackMatch.trackNameMatch,
      artistNameMatch: artistInTitle,
      channelMatchesArtist: artistInChannel,
      isOfficialContent: channelIsVevo || channelIsTopic || hasOfficialInChannel,
      penaltyApplied: penalties,
      normalizedVideoTitle: normalizedTitle,
      normalizedTrackName: bestTrackMatch.normalizedTrackName,
      similarityScore: bestTrackMatch.similarityScore,
    };

    return {
      ...video,
      matchScore: score,
      matchDetails,
    };
  });

  scoredVideos.sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));

  return scoredVideos;
}

export function filterRelevantVideos(
  videos: YouTubeVideo[],
  spotifyTracks: SpotifyTrack[],
  artistName: string,
  minScore: number = 50
): YouTubeVideo[] {
  const scored = matchYouTubeToSpotify(videos, spotifyTracks, artistName);
  return scored.filter(v => (v.matchScore || 0) >= minScore);
}
